/**
 * Recompute a backer's pro-rata vault earnings.
 *
 * Mirrors the contract's burn-to-claim payout formula at
 * `contracts/markets/programs/markets/src/instructions/post_resolution/claim_backer_fees.rs:35-39`:
 *
 *   earned = (userShare × vaultBalance) / supply
 *
 * Multiply-before-divide on bigint preserves D6 precision (JS `number`
 * loses precision above 2^53; a fully-claimed mainnet market could
 * easily exceed that). Returns `prev` unchanged when nothing observable
 * changes — keeps structural-sharing short-circuits intact for
 * downstream `<Items>` / `useGetXField` consumers.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

export interface BuiltBackerEarningsRow {
  readonly vaultD6?: string;
  readonly supplyD6?: string;
  readonly userShareD6?: string;
  readonly earnedD6?: string;
  readonly totalCollectedD6?: string;
  readonly claimedTotalD6?: string;
  readonly canClaim?: boolean;
  readonly marketState?: string;
}

function safeBigInt(value: string | undefined, fallback: bigint = 0n): bigint {
  if (value === undefined) return fallback;
  try {
    return BigInt(value);
  } catch {
    return fallback;
  }
}

/**
 * Apply one realtime patch to a `useGetMarketBackerEarnings` row.
 *
 * Called after the declarative `set:` / `add:` recipes have already
 * mutated `vaultD6` / `supplyD6` / `userShareD6` / `totalCollectedD6`.
 * This helper closes the loop by recomputing `earnedD6` from the
 * freshly-patched fields AND by recomputing `canClaim` (which depends
 * on `userShareD6 > 0` and `marketState ∈ {resolved, graduated}`).
 *
 * The wire payload (`raw`) is ignored — every field this helper needs
 * already lives on `prev`. The helper just stitches the derived
 * `earnedD6` + `canClaim` from those fields.
 */
export function recomputeBackerEarnings<T extends BuiltBackerEarningsRow>(
  prev: T,
  _raw: Record<string, unknown>,
): T {
  const supply = safeBigInt(prev.supplyD6);
  if (supply === 0n) {
    if (prev.earnedD6 === '0' && prev.canClaim === false) return prev;
    return { ...prev, earnedD6: '0', canClaim: false };
  }
  const userShare = safeBigInt(prev.userShareD6);
  const vault = safeBigInt(prev.vaultD6);
  const earned = (userShare * vault) / supply;
  const nextEarned = earned.toString();
  const isResolved = prev.marketState === 'resolved' || prev.marketState === 'graduated';
  const nextCanClaim = userShare > 0n && isResolved;
  if (prev.earnedD6 === nextEarned && prev.canClaim === nextCanClaim) return prev;
  return {
    ...prev,
    earnedD6: nextEarned,
    canClaim: nextCanClaim,
  };
}
